import dayjs from 'dayjs';
import { fetchWeeklyComputations } from './computations';
import { getRecords } from './records';

const PAGE_SIZE = 50;
const WEEKLY_BATCH_SIZE = 8;
const ACTIVITY_STALE_TIME = 60 * 1000;

const fetchExerciseRecords = async ({ patientUuid, planUuid, startDate, endDate }) => {
    const records = [];
    const seenTokens = new Set();
    let pageToken = null;
    let total = null;
    let hasMore = true;

    while (hasMore) {
        const response = await getRecords({
            patient: patientUuid,
            plan: planUuid,
            type: 'EXERCISE',
            pageLimit: PAGE_SIZE,
            pageToken,
            onlyUploaded: false,
            qi: startDate,
            df: endDate
        });
        const page = Array.isArray(response?.data) ? response.data : [];
        records.push(...page);

        const responseTotal = Number(response?.headers?.['pagination-count']);
        if (Number.isFinite(responseTotal)) total = responseTotal;

        const nextToken = response?.headers?.['pagination-token'];
        if (
            page.length === 0 ||
            (total != null && records.length >= total) ||
            !nextToken ||
            seenTokens.has(nextToken)
        ) {
            hasMore = false;
        } else {
            seenTokens.add(nextToken);
            pageToken = nextToken;
        }
    }

    return records;
};

const getCachedExerciseRecords = ({
    queryClient,
    patientUuid,
    planUuid,
    startDate,
    endDate
}) => {
    const queryFn = () =>
        fetchExerciseRecords({ patientUuid, planUuid, startDate, endDate });

    return queryClient
        ? queryClient.fetchQuery({
              queryKey: [
                  'patient-exercise-records',
                  patientUuid,
                  planUuid || 'all-plans',
                  dayjs(startDate).format('YYYY-MM-DD'),
                  dayjs(endDate).format('YYYY-MM-DD')
              ],
              queryFn,
              staleTime: ACTIVITY_STALE_TIME
          })
        : queryFn();
};

const buildActivityPeriods = (startDate, endDate) => {
    const completedPeriods = [];
    const end = dayjs(endDate);
    let periodStart = dayjs(startDate).startOf('day');

    while (!periodStart.add(7, 'day').isAfter(end)) {
        completedPeriods.push(periodStart.format('YYYY-MM-DD'));
        periodStart = periodStart.add(7, 'day');
    }

    return {
        completedPeriods,
        partialPeriodStart: periodStart.format('YYYY-MM-DD')
    };
};

const getCachedWeeklySummary = ({ queryClient, patientUuid, planUuid, startDate }) => {
    const queryFn = () =>
        fetchWeeklyComputations({
            qi: startDate,
            patient: patientUuid,
            plan: planUuid
        });

    return queryClient
        ? queryClient.fetchQuery({
              queryKey: [
                  'patient-weekly-activity',
                  patientUuid,
                  planUuid || 'all-plans',
                  startDate
              ],
              queryFn,
              staleTime: ACTIVITY_STALE_TIME
          })
        : queryFn();
};

const fetchWeeklySummaries = async ({
    queryClient,
    patientUuid,
    planUuid,
    periodStarts
}) => {
    const summaries = [];

    for (let index = 0; index < periodStarts.length; index += WEEKLY_BATCH_SIZE) {
        const batch = periodStarts.slice(index, index + WEEKLY_BATCH_SIZE);
        const responses = await Promise.all(
            batch.map((startDate) =>
                getCachedWeeklySummary({
                    queryClient,
                    patientUuid,
                    planUuid,
                    startDate
                })
            )
        );
        summaries.push(
            ...responses.map((data, responseIndex) => ({
                startDate: batch[responseIndex],
                data
            }))
        );
    }

    return summaries;
};

const sumValues = (values) =>
    Object.values(values || {}).reduce((sum, value) => sum + Number(value || 0), 0);

const summarizeWeeklyData = (summaries) =>
    summaries.reduce(
        (totals, summary) => ({
            completed:
                totals.completed + sumValues(summary?.data?.exercises?.done_in_plan),
            missed: totals.missed + sumValues(summary?.data?.exercises?.pending_in_plan)
        }),
        { completed: 0, missed: 0 }
    );

const toPeriodSummary = ({ startDate, endDate, completed, missed }) => ({
    startDate,
    endDate,
    completed,
    missed,
    total: completed + missed
});

const groupRecordsByDay = ({ records, startDate, endDate }) => {
    const recordsByDate = records.reduce((groups, record) => {
        const dateKey = dayjs(record.ti).format('YYYY-MM-DD');
        const current = groups.get(dateKey) || { completed: 0, missed: 0 };
        current[record.uploaded ? 'completed' : 'missed'] += 1;
        groups.set(dateKey, current);
        return groups;
    }, new Map());
    const periods = [];
    let date = dayjs(startDate).startOf('day');
    const end = dayjs(endDate);

    while (date.isBefore(end)) {
        const dateKey = date.format('YYYY-MM-DD');
        const values = recordsByDate.get(dateKey) || { completed: 0, missed: 0 };
        periods.push(
            toPeriodSummary({
                startDate: dateKey,
                endDate: date.add(1, 'day').format('YYYY-MM-DD'),
                ...values
            })
        );
        date = date.add(1, 'day');
    }

    return periods;
};

export const getActivityPeriodSummary = async ({
    queryClient,
    patientUuid,
    planUuid,
    startDate,
    endDate = dayjs(),
    groupPartialByDay = false
}) => {
    const start = dayjs(startDate).startOf('day');
    const end = dayjs(endDate);
    const { completedPeriods, partialPeriodStart } = buildActivityPeriods(start, end);
    const [weeklySummaries, partialRecords] = await Promise.all([
        fetchWeeklySummaries({
            queryClient,
            patientUuid,
            planUuid,
            periodStarts: completedPeriods
        }),
        dayjs(partialPeriodStart).isBefore(end)
            ? getCachedExerciseRecords({
                  queryClient,
                  patientUuid,
                  planUuid,
                  startDate: partialPeriodStart,
                  endDate: end
              })
            : []
    ]);
    const weeklyTotals = summarizeWeeklyData(weeklySummaries);
    const relevantRecords = partialRecords.filter((record) => {
        const recordDate = dayjs(record.ti);
        if (
            !recordDate.isValid() ||
            recordDate.isBefore(start) ||
            !recordDate.isBefore(end)
        ) {
            return false;
        }

        return planUuid
            ? record.patient_plan?.uuid === planUuid
            : Boolean(record.patient_plan);
    });
    const currentCompleted = relevantRecords.filter((record) => record.uploaded).length;
    const currentMissed = relevantRecords.length - currentCompleted;
    const completed = weeklyTotals.completed + currentCompleted;
    const missed = weeklyTotals.missed + currentMissed;
    const weeklyPeriods = weeklySummaries.map((summary) => {
        const periodCompleted = sumValues(summary.data?.exercises?.done_in_plan);
        const periodMissed = sumValues(summary.data?.exercises?.pending_in_plan);

        return toPeriodSummary({
            startDate: summary.startDate,
            endDate: dayjs(summary.startDate).add(7, 'day').format('YYYY-MM-DD'),
            completed: periodCompleted,
            missed: periodMissed
        });
    });
    const partialPeriods = groupPartialByDay
        ? groupRecordsByDay({
              records: relevantRecords,
              startDate: partialPeriodStart,
              endDate: end
          })
        : dayjs(partialPeriodStart).isBefore(end)
          ? [
                toPeriodSummary({
                    startDate: partialPeriodStart,
                    endDate: dayjs(end).format('YYYY-MM-DD'),
                    completed: currentCompleted,
                    missed: currentMissed
                })
            ]
          : [];

    return {
        completed,
        missed,
        total: completed + missed,
        periods: [...weeklyPeriods, ...partialPeriods]
    };
};

export const getLatestExerciseSession = async ({
    patientUuid,
    planUuid,
    startDate,
    endDate = dayjs()
}) => {
    const response = await getRecords({
        patient: patientUuid,
        plan: planUuid,
        type: 'EXERCISE',
        pageLimit: 1,
        pageToken: null,
        onlyUploaded: true,
        qi: startDate,
        df: endDate
    });

    return response?.data?.[0] || null;
};
